import { typography, radius } from '@/shared/theme/tokens';
import { Sparkline } from '../../ui/Sparkline';
import { LIVE_STATUS_META, CATEGORY_LABEL, withAlpha } from '../../liveModeMeta';
import { BOARD } from './boardTheme';
import { SplitFlap } from './SplitFlap';
import { SectionHeader, Empty } from './BoardChrome';

const MONO = BOARD.mono;
const VISIBLE = 5;

const STALE_META = { color: BOARD.inkFaint, label: 'STALE' };
const COLUMNS = [
  { label: 'Status', flex: 0.85 },
  { label: 'Parameter', flex: 1.6 },
  { label: 'Trend', flex: 0.7 },
  { label: 'Reading', flex: 1, align: 'right' },
];

/**
 * Alerts departures board — ranked monitor rows; colour tracks each value's
 * CURRENT standing (a recovered metric reads OK/green). The reading renders as a
 * split-flap counter; stale rows grey out, show "——", and never flash. The one
 * row the model just flagged (flashId) gets a single status-tinted pulse.
 */
export function AlertBoard({ alerts = [], flashId, animate, onSelect }) {
  const shown = alerts.slice(0, VISIBLE);
  const overflow = alerts.length - shown.length;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', background: BOARD.board }}>
      <SectionHeader title="Monitor" count={alerts.length} accent={BOARD.cyan} columns={shown.length ? COLUMNS : []} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {shown.length === 0 ? (
          <Empty icon="check_circle" color={BOARD.ok} text="All Clear" sub="No parameters are off-range or trending toward a breach." />
        ) : (
          <>
            {shown.map((alert) => (
              <AlertRow key={alert.id} alert={alert} flashing={alert.id === flashId} animate={animate} onSelect={onSelect} />
            ))}
            {overflow > 0 && (
              <div style={{
                fontFamily: MONO, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold,
                color: BOARD.inkFaint, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase',
                padding: '12px 16px', borderTop: `1px solid ${BOARD.seam}`, fontVariantNumeric: 'tabular-nums',
              }}>
                +{overflow} more monitored
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function AlertRow({ alert, flashing, animate, onSelect }) {
  const stale = !!alert.isStale;
  const meta = stale ? STALE_META : (LIVE_STATUS_META[alert.status] ?? LIVE_STATUS_META.ok);
  const isFlashing = flashing && !stale;
  const clickable = typeof onSelect === 'function';
  const reading = Number.isFinite(alert.liveValue) ? Math.round(alert.liveValue) : alert.liveValue;

  return (
    <div
      onClick={clickable ? () => onSelect(alert) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(alert); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${alert.parameterName} alert` : undefined}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
        borderBottom: `1px solid ${BOARD.seam}`,
        opacity: stale ? 0.55 : 1, cursor: clickable ? 'pointer' : 'default',
      }}
    >
      {/* single-pulse flash tint — only ever on the row the model just changed */}
      {isFlashing && (
        <span aria-hidden style={{
          position: 'absolute', inset: 0, background: withAlpha(meta.color, '22'),
          animation: animate ? 'board-pulse 0.45s steps(1,end) 2' : 'none', pointerEvents: 'none',
        }} />
      )}

      {/* STATUS */}
      <div style={{ flex: 0.85, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
        <span style={{
          width: 16, height: 30, borderRadius: radius.xs, flexShrink: 0,
          background: stale ? 'transparent' : meta.color,
          border: stale ? `1px solid ${BOARD.rule}` : 'none',
          boxShadow: isFlashing ? `0 0 12px ${meta.color}` : 'none',
        }} />
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.wide, color: meta.color,
        }}>
          {meta.label}
        </span>
      </div>

      {/* PARAMETER */}
      <div style={{ flex: 1.6, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['3xl'],
          fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
          textTransform: 'uppercase', color: BOARD.ink,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {alert.parameterName}
        </span>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, color: BOARD.inkFaint,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
        }}>
          {CATEGORY_LABEL[alert.category] ?? alert.category}
        </span>
      </div>

      {/* TREND */}
      <div style={{ flex: 0.7, minWidth: 0, opacity: stale ? 0.4 : 0.95, position: 'relative' }}>
        <Sparkline data={alert.buffer} color={meta.color} height={30} animate={false} />
      </div>

      {/* READING */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, position: 'relative' }}>
        {stale ? (
          <SplitFlap value="——" color={BOARD.inkFaint} size="lg" animate={false} dim />
        ) : (
          <>
            <SplitFlap value={reading} color={meta.color} size="lg" animate={animate} />
            {alert.unit && (
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: BOARD.inkFaint }}>{alert.unit}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
